import React, { useEffect, useState } from 'react';
import { api } from '@/lib/db';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, Button, Input, TextArea } from '@/components/ui/Components';
import SafeIcon from '@/common/SafeIcon';

// Magyar karakterek normalizálása slug-hoz
function generateSlug(name) {
  const huMap = {
    'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ö': 'o', 'ő': 'o',
    'ú': 'u', 'ü': 'u', 'ű': 'u',
    'Á': 'a', 'É': 'e', 'Í': 'i', 'Ó': 'o', 'Ö': 'o', 'Ő': 'o',
    'Ú': 'u', 'Ü': 'u', 'Ű': 'u',
  };
  return name
    .toLowerCase()
    .replace(/[áéíóöőúüűÁÉÍÓÖŐÚÜŰ]/g, c => huMap[c] || c)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const LocationEditCard = ({ loc, fetchData, onCopyLink }) => {
  const { addToast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({ ...loc });
  const [saving, setSaving] = useState(false);

  const hasChanges = Object.keys(formData).some(key => formData[key] !== loc[key]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateLocation(loc.id, formData);
      await fetchData();
      addToast('Helyszín mentve!', 'success');
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenLink = (e) => {
    e.preventDefault();
    const url = `${window.location.origin}/#/r/${formData.slug}`;
    window.open(url, '_blank');
  };

  return (
    <Card className="p-6 hover:shadow-md transition-shadow">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden flex items-center justify-center border border-gray-200 dark:border-gray-600">
              {formData.logo_url ? (
                <img src={formData.logo_url} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <SafeIcon name="Image" className="text-gray-400 dark:text-gray-500" />
              )}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{formData.name}</h3>
              <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">/{formData.slug}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setIsExpanded(!isExpanded)}>
            <SafeIcon name={isExpanded ? "ChevronUp" : "ChevronDown"} className="w-4 h-4 mr-2" />
            {isExpanded ? "Kevesebb" : "Beállítások"}
          </Button>
        </div>

        {isExpanded && (
          <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700 space-y-8 animate-in slide-in-from-top-2 duration-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Input
                  label="Google értékelési link (ide kerülnek a 4-5 csillagos értékelések)"
                  value={formData.google_review_url}
                  onChange={e => handleChange('google_review_url', e.target.value)}
                />
              </div>
              <div>
                <Input label="Logó URL" value={formData.logo_url} onChange={e => handleChange('logo_url', e.target.value)} />
              </div>
              <Input label="Márkaszín" type="color" className="h-10" value={formData.accent_color} onChange={e => handleChange('accent_color', e.target.value)} />
            </div>

            <div className="bg-blue-50/50 dark:bg-blue-900/20 p-6 rounded-2xl border border-blue-100 dark:border-blue-800">
              <h4 className="font-bold text-blue-900 dark:text-blue-300 mb-4 flex items-center gap-2">
                <SafeIcon name="ShieldCheck" className="w-5 h-5" /> Kapcsolati adatok (GDPR-hoz)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input label="Cégnév" value={formData.company_name} onChange={e => handleChange('company_name', e.target.value)} />
                <Input label="E-mail" value={formData.email} onChange={e => handleChange('email', e.target.value)} />
                <Input label="Telefon" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-gray-700 dark:text-gray-300 mb-3">Módosítható szövegek</h4>
                <div className="space-y-4">
                  <Input label="Főcímsor" value={formData.headline} onChange={e => handleChange('headline', e.target.value)} />
                  <Input label="Leírás" value={formData.description} onChange={e => handleChange('description', e.target.value)} />
                </div>
              </div>
              <div>
                <h4 className="font-bold text-gray-700 dark:text-gray-300 mb-3">Visszajelzés űrlap</h4>
                <div className="space-y-4">
                  <Input label="Gomb felirat" value={formData.button_text} onChange={e => handleChange('button_text', e.target.value)} />
                  <Input label="Sikeres űrlap kitöltés szövege" value={formData.success_headline} onChange={e => handleChange('success_headline', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-gray-700">
              <Button variant="danger" size="sm" onClick={() => { if (confirm('Biztosan törlöd ezt a profilt?')) api.deleteLocation(loc.id).then(fetchData); }}>
                <SafeIcon name="Trash2" className="w-4 h-4 mr-1" /> Profil törlése
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className={hasChanges ? '' : 'opacity-50'}
              >
                <SafeIcon name="Save" className="w-4 h-4 mr-2" />
                {saving ? 'Mentés...' : hasChanges ? 'Változások mentése' : 'Mentve'}
              </Button>
            </div>
          </div>
        )}

        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-xl flex items-center justify-between mt-4 border border-gray-100 dark:border-gray-600">
          <span className="text-xs font-mono text-gray-400 dark:text-gray-500 truncate mr-4">
            {window.location.origin}/#/r/{formData.slug}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onCopyLink(formData.slug)}>
              <SafeIcon name="Copy" className="w-4 h-4" />
            </Button>
            <button
              onClick={handleOpenLink}
              className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              title="Megnyitás új lapon"
            >
              <SafeIcon name="ExternalLink" className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
};

const Locations = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [locations, setLocations] = useState([]);
  const [business, setBusiness] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newLoc, setNewLoc] = useState({
    name: '',
    google_review_url: '',
    accent_color: '#3b82f6',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const [locs, biz] = await Promise.all([
      api.getLocations(user.business_id),
      api.getBusinessById(user.business_id),
    ]);
    setLocations(locs);
    setBusiness(biz);
  };

  const locationLimit = business?.location_limit ?? null;
  const atLimit = locationLimit !== null && locations.length >= locationLimit;

  const handleNameChange = (name) => {
    setNewLoc(prev => ({
      ...prev,
      name,
      slug: generateSlug(name),
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (atLimit) {
      addToast(`Elérted a helyszín limitet (${locationLimit}). Lépj velünk kapcsolatba a bővítéshez!`, 'error');
      return;
    }
    try {
      await api.addLocation(user.business_id, { ...newLoc, slug: generateSlug(newLoc.name) });
      addToast('Sikeresen létrehozva!');
      setShowAdd(false);
      setNewLoc({ name: '', google_review_url: '', accent_color: '#3b82f6' });
      fetchData();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-gray-900 dark:text-white">Értékelhető cégprofilok</h1>
          {locationLimit !== null && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {locations.length} / {locationLimit} létrehozott cégprofilok
            </p>
          )}
        </div>
        <Button
          onClick={() => setShowAdd(true)}
          disabled={atLimit}
          title={atLimit ? `Elérted a ${locationLimit} profil limitet` : undefined}
        >
          <SafeIcon name="Plus" className="mr-2" /> Új profil hozzáadása
        </Button>
      </div>

      {/* ─── Limit figyelmeztetés ─────────────────────────────────────── */}
      {atLimit && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4 flex items-start gap-3">
          <SafeIcon name="AlertTriangle" className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-orange-800 dark:text-orange-300">Elérted a hozzárendelhető limitet ({locationLimit})</p>
            <p className="text-sm text-orange-700 dark:text-orange-400 mt-0.5">Több cég kezeléséhez lépj velünk kapcsolatba.</p>
          </div>
        </div>
      )}

      {showAdd && !atLimit && (
        <Card className="p-8 border-2 border-blue-600">
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="Megnevezés"
                value={newLoc.name}
                onChange={e => handleNameChange(e.target.value)}
                required
              />
              {newLoc.name && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-mono">→ /r/{generateSlug(newLoc.name)}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <Input label="Google értékelési link (ide kerülnek a 4-5 csillagos értékelések)" value={newLoc.google_review_url} onChange={e => setNewLoc({ ...newLoc, google_review_url: e.target.value })} required />
            </div>
            <div className="md:col-span-2 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Mégse</Button>
              <Button type="submit">Helyszín Mentése</Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6">
        {locations.map(loc => (
          <LocationEditCard
            key={loc.id}
            loc={loc}
            fetchData={fetchData}
            onCopyLink={(s) => {
              navigator.clipboard.writeText(`${window.location.origin}/#/r/${s}`);
              addToast('Link másolva a vágólapra!');
            }}
          />
        ))}
        {locations.length === 0 && !showAdd && (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700">
            <SafeIcon name="MapPin" className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Nincs még cégprofil létrehozva</h3>
            <p className="text-gray-500 dark:text-gray-400">Kattints az "Új profil hozzáadása" gombra az indításhoz.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Locations;
